import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation } from "wouter";
import { UserButton } from "./UserMenu";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import {
  ChevronRight, ChevronsLeft, LayoutGrid, Search, ChevronDown,
  Building2, Users, Shield, ScrollText, CreditCard, IndianRupee, X, Check, RefreshCw,
  Package, TrendingUp, FolderKanban, MonitorSmartphone, BarChart3, ShoppingBag, Briefcase, ShieldCheck, Factory, CheckSquare, Award, Headphones, Plug, FolderOpen, GraduationCap, Plane, UserCircle, Menu, Activity, ShieldAlert, Sun, Moon, LifeBuoy, Lock,
} from "lucide-react";
import { useTheme } from "@/providers/ThemeProvider";
import { useGetMe, customFetch } from "@workspace/api-client-react";
import NotificationBell from "./NotificationBell";
import { AttendanceHeaderWidget } from "./AttendanceHeaderWidget";
import { searchPages } from "@/lib/searchIndex";
import { usePermissions } from "@/lib/permissions";
import { modulesForPath } from "@/lib/routeModules";
import { UpgradePackageModal } from "./UpgradePackageModal";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface AppShellProps {
  children: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  title?: string;
}

interface TenantItem {
  tenantId: number;
  name: string;
  slug: string;
  logoUrl: string | null;
  primaryColor: string | null;
}

// Each rail item declares the permission module(s) that grant access. A `null`
// `modules` means the item is always visible (dashboard + self-service portal).
// Modules map to the backend `requirePermission(module, …)` checks for each
// feature's routes (see api-server route handlers).
// `superAdminOnly` items are only shown to platform super-admins.
interface RailItem {
  id: string;
  icon: typeof LayoutGrid;
  label: string;
  href: string;
  modules: string[] | null;
  superAdminOnly?: boolean;
}

const MODULE_RAIL: RailItem[] = [
  { id: "dashboard", icon: LayoutGrid, label: "Dashboard", href: "/dashboard", modules: null },
  { id: "employee", icon: UserCircle, label: "My Portal", href: "/employee", modules: null },
  { id: "organization", icon: Building2, label: "Organization", href: "/organization", modules: ["organization"] },
  { id: "users", icon: Shield, label: "Users & Roles", href: "/users", modules: ["users", "roles"] },
  { id: "hr", icon: Users, label: "HR", href: "/hr", modules: ["hr"] },
  { id: "recruitment", icon: Briefcase, label: "Recruitment", href: "/recruitment", modules: ["hr"] },
  { id: "performance", icon: Award, label: "Performance", href: "/performance", modules: null },
  { id: "finance", icon: CreditCard, label: "Finance", href: "/finance", modules: ["finance"] },
  { id: "payroll", icon: IndianRupee, label: "Payroll", href: "/payroll", modules: ["payroll"] },
  { id: "inventory", icon: Package, label: "Inventory", href: "/inventory", modules: ["inventory"] },
  { id: "procurement", icon: ShoppingBag, label: "Procurement", href: "/procurement", modules: ["finance"] },
  { id: "crm", icon: TrendingUp, label: "CRM", href: "/crm", modules: ["crm"] },
  { id: "operations", icon: FolderKanban, label: "Operations", href: "/operations", modules: ["operations"] },
  { id: "quality", icon: ShieldCheck, label: "Quality", href: "/quality", modules: ["quality"] },
  { id: "manufacturing", icon: Factory, label: "Manufacturing", href: "/manufacturing", modules: ["manufacturing"] },
  { id: "service", icon: Headphones, label: "Customer Service", href: "/service", modules: ["operations"] },
  { id: "dms", icon: FolderOpen, label: "Documents", href: "/dms", modules: ["dms"] },
  { id: "admin", icon: MonitorSmartphone, label: "Admin & IT", href: "/admin", modules: ["admin"] },
  { id: "ld", icon: GraduationCap, label: "L&D", href: "/ld", modules: ["hr"] },
  { id: "travel", icon: Plane, label: "Travel & Expense", href: "/travel", modules: ["finance", "hr"] },
  { id: "reports", icon: BarChart3, label: "MIS & Reports", href: "/reports", modules: ["reports"] },
  { id: "approvals", icon: CheckSquare, label: "Approvals", href: "/approvals", modules: null },
  { id: "helpdesk", icon: LifeBuoy, label: "Help Desk", href: "/helpdesk", modules: null },
  { id: "integrations", icon: Plug, label: "Integrations", href: "/integrations", modules: ["finance"] },
  { id: "audit", icon: ScrollText, label: "Audit Log", href: "/audit-log", modules: ["audit_log"] },
];

/**
 * Map rail item → SaaS package `allowedModules` keys.
 * Rail ids often differ from package keys (e.g. recruitment → ats, organization → admin).
 * `null` = no package gate (dashboard / self-service / approvals).
 */
function packageKeysForRail(item: Pick<RailItem, "id" | "modules">): string[] | null {
  if (item.modules === null) return null;

  const RAIL_TO_PACKAGE: Record<string, string[]> = {
    organization: ["admin"],
    users: ["admin"],
    recruitment: ["ats"],
    performance: ["hr"],
    ld: ["hr"],
    dms: ["admin"],
    reports: ["bi"],
    audit: ["admin"],
    integrations: ["finance"],
    travel: ["travel"],
    procurement: ["procurement"],
    service: ["service"],
  };

  return RAIL_TO_PACKAGE[item.id] ?? [item.id];
}

function isModulePackageLocked(
  item: Pick<RailItem, "id" | "modules">,
  allowedModules: string[],
): boolean {
  const keys = packageKeysForRail(item);
  if (keys === null) return false;
  return !keys.some((k) => allowedModules.includes(k));
}

interface SearchResult {
  id: string;
  type: string;
  label: string;
  sublabel?: string;
  href: string;
}

interface SearchGroup {
  group: string;
  items: SearchResult[];
}

export default function AppShell({ children, breadcrumbs }: AppShellProps) {
  const { theme, toggleTheme } = useTheme();
  const { data: me, isPending: mePending } = useGetMe();
  const { canSee, isSuperAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const [location, navigate] = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [railExpanded, setRailExpanded] = useState<boolean>(() => localStorage.getItem("railExpanded") === "true");
  const [lockedModalOpen, setLockedModalOpen] = useState(false);
  const [lockedModuleName, setLockedModuleName] = useState("");

  const { data: packageRes } = useQuery<{ package: { name: string; code: string; allowedModules: string[] } }>({
    queryKey: ["tenant-package"],
    queryFn: () => customFetch<{ package: { name: string; code: string; allowedModules: string[] } }>("/api/tenant/current-package"),
    staleTime: 60 * 1000,
  });

  const tenantPackage = packageRes?.package;
  const allowedModules = useMemo(() => {
    return tenantPackage?.allowedModules || ["hr", "admin", "employee", "payroll", "helpdesk", "travel", "finance", "crm", "procurement", "inventory", "operations", "manufacturing", "quality", "ats", "bi", "service"];
  }, [tenantPackage]);

  const searchRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Check whether Tally integration is enabled — nav item only shown when on.
  // Only fetch for users with finance permission to avoid constant 403 errors.
  const canSeeFinance = canSee(["finance"]);
  const { data: tallyConfig } = useQuery<{ enabled: boolean } | null>({
    queryKey: ["tally-config"],
    queryFn: () => customFetch<{ enabled: boolean } | null>("/api/tally/config"),
    staleTime: 60 * 1000,
    enabled: canSeeFinance,
  });
  const tallyEnabled = tallyConfig?.enabled === true;

  // Fetch all tenants the current user belongs to (active memberships)
  const { data: tenantsData } = useQuery<TenantItem[]>({
    queryKey: ["me", "tenants"],
    queryFn: () => customFetch<TenantItem[]>("/api/me/tenants"),
    staleTime: 5 * 60 * 1000,
  });

  // Ensure tenants is always an array
  const tenants = Array.isArray(tenantsData) ? tenantsData : [];

  // Derive selected tenant ID from localStorage (set by setTenantIdGetter in App.tsx)
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(() => {
    const stored = localStorage.getItem("selectedTenantId");
    return stored ? Number(stored) : null;
  });

  const companyName = me?.tenantName ?? "Alea ERP";
  const logoUrl = me?.tenantLogoUrl ?? null;
  const primaryColor = me?.tenantPrimaryColor ?? null;
  const shellBg = primaryColor ?? "#0a2d4a";
  const railBg = primaryColor
    ? `color-mix(in srgb, ${primaryColor} 80%, black)`
    : "#071f34";

  // Phase 8+9: Apply tenant portal name as document title and dynamic favicon at runtime.
  const portalName = (me as unknown as Record<string, string | null> | undefined)?.tenantPortalName ?? null;
  const faviconUrl = (me as unknown as Record<string, string | null> | undefined)?.tenantFaviconUrl ?? null;
  useEffect(() => {
    document.title = portalName ?? companyName ?? "Alea ERP";
  }, [portalName, companyName]);
  useEffect(() => {
    if (!faviconUrl) return;
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }, [faviconUrl]);

  const isLaunchpad = location === "/dashboard" || location === "/";

  // RBAC: rail items the current user is permitted to see (super admins see all).
  const visibleRail = useMemo<RailItem[]>(() => {
    const items: RailItem[] = [
      ...MODULE_RAIL,
      ...(tallyEnabled
        ? [{ id: "tally", icon: RefreshCw, label: "Tally Sync", href: "/tally", modules: ["finance"] } as RailItem]
        : []),
    ];
    return items.filter((item) => {
      if (item.superAdminOnly && !isSuperAdmin) return false;
      return canSee(item.modules);
    });
  }, [canSee, tallyEnabled, isSuperAdmin]);

  // RBAC: whether the current route is permitted for this user.
  const routeAllowed = canSee(modulesForPath(location));

  // Debounce the search query by 200ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery), 200);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(-1);
  }, [debouncedQuery]);

  // API search results
  const { data: apiData, isFetching } = useQuery<{ results: SearchResult[] }>({
    queryKey: ["global-search", debouncedQuery],
    queryFn: () => customFetch<{ results: SearchResult[] }>(`/api/search?q=${encodeURIComponent(debouncedQuery)}`),
    enabled: debouncedQuery.length >= 2,
    staleTime: 10 * 1000,
  });

  // Client-side page & sub-section (settings/tabs) matches — covers every page
  // and setting in the app, deep-linking straight to the relevant tab.
  const pageMatches = useMemo<SearchResult[]>(() => {
    return searchPages(debouncedQuery, 8)
      // RBAC: never surface pages the user can't access (mirrors the sidebar /
      // route guard). Resolve each page's required modules from its href.
      .filter((p) => canSee(modulesForPath(p.href)))
      .map((p) => ({
        id: p.id,
        type: "page",
        label: p.label,
        sublabel: p.label === p.module ? "Page" : p.module,
        href: p.href,
      }));
  }, [debouncedQuery, canSee]);

  // Build grouped results. Order matters — it's the display order in the dropdown.
  const groups = useMemo<SearchGroup[]>(() => {
    const apiResults = apiData?.results ?? [];
    const grouped: Record<string, SearchResult[]> = {
      Pages: pageMatches,
      Employees: apiResults.filter((r) => r.type === "employee"),
      Departments: apiResults.filter((r) => r.type === "department"),
      Designations: apiResults.filter((r) => r.type === "designation"),
      Branches: apiResults.filter((r) => r.type === "branch"),
      Vendors: apiResults.filter((r) => r.type === "vendor"),
      CRM: apiResults.filter((r) => r.type === "crm_account" || r.type === "crm_contact"),
      Products: apiResults.filter((r) => r.type === "product"),
      Jobs: apiResults.filter((r) => r.type === "job"),
      "Approval Rules": apiResults.filter((r) => r.type === "approval_rule"),
    };
    return Object.entries(grouped)
      .filter(([, items]) => items.length > 0)
      .map(([group, items]) => ({ group, items }));
  }, [pageMatches, apiData]);

  // Flat list for keyboard navigation
  const flatResults = useMemo<SearchResult[]>(() => groups.flatMap((g) => g.items), [groups]);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery("");
    setDebouncedQuery("");
    setSelectedIndex(-1);
  }, []);

  const navigateTo = useCallback((href: string) => {
    navigate(href);
    closeSearch();
  }, [navigate, closeSearch]);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  // Global ⌘K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!searchOpen) return;
    const close = (e: MouseEvent) => {
      if (
        searchRef.current && !searchRef.current.closest("[data-search-container]")?.contains(e.target as Node)
      ) {
        closeSearch();
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [searchOpen, closeSearch]);

  useEffect(() => {
    if (!companySwitcherOpen) return;
    const close = () => setCompanySwitcherOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [companySwitcherOpen]);

  function handleSwitchTenant(tenantId: number) {
    if (tenantId === selectedTenantId) {
      setCompanySwitcherOpen(false);
      return;
    }
    // Persist selection so setTenantIdGetter picks it up for all subsequent API calls
    localStorage.setItem("selectedTenantId", String(tenantId));
    setSelectedTenantId(tenantId);
    setCompanySwitcherOpen(false);
    // Invalidate all cached data so the new tenant's data is fetched fresh
    queryClient.invalidateQueries();
  }

  // Determine the display name for the currently-active company in the switcher header
  const activeTenant = tenants?.find((t) => t.tenantId === selectedTenantId) ?? tenants?.[0];
  const displayName = activeTenant?.name ?? companyName;
  const displayColor = activeTenant?.primaryColor ?? shellBg;

  return (
    <div className="flex flex-col h-[100dvh] bg-background">
      {/* SAP Fiori-style Shell Bar */}
      <header
        className="flex-shrink-0 h-12 flex items-center px-3 sm:px-4 gap-2 sm:gap-3 shadow-sm z-30 bg-card text-foreground border-b border-border"
      >
        {/* Mobile hamburger */}
        <button
          className={`md:hidden flex items-center justify-center h-8 w-8 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0 ${searchOpen ? 'hidden' : ''}`}
          onClick={() => setMobileSidebarOpen(true)}
          aria-label="Open navigation"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Company Switcher */}
        <div className={`relative ${searchOpen ? 'hidden md:block' : ''}`} onClick={(e) => e.stopPropagation()}>
          <button
            data-testid="company-switcher-btn"
            onClick={() => setCompanySwitcherOpen((v) => !v)}
            className="flex items-center gap-2 group"
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={displayName}
                className="h-7 w-7 rounded object-contain bg-muted p-0.5"
              />
            ) : (
              <div className="h-7 w-7 rounded bg-muted flex items-center justify-center">
                <span className="text-foreground font-bold text-xs">{displayName[0]}</span>
              </div>
            )}
            <span className="text-foreground font-semibold text-sm tracking-wide group-hover:text-foreground/80 transition-colors hidden sm:block max-w-[150px] truncate">
              {displayName}
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground/80 transition-colors hidden sm:block" />
          </button>

          {companySwitcherOpen && (
            <div data-testid="company-switcher-dropdown" className="absolute top-full left-0 mt-2 w-72 bg-card dark:bg-slate-800 rounded-lg shadow-xl border border-border dark:border-slate-700 z-50 overflow-hidden">
              <div className="px-4 py-3 bg-muted/50 dark:bg-slate-900 border-b border-border dark:border-slate-700">
                <p className="text-[10px] font-semibold text-muted-foreground dark:text-slate-500 uppercase tracking-wider mb-1">
                  Active Company
                </p>
                <div className="flex items-center gap-2.5">
                  <div
                    className="h-8 w-8 rounded-md flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ backgroundColor: displayColor }}
                  >
                    {displayName[0]}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground dark:text-slate-100 truncate">{displayName}</p>
                    <p className="text-xs text-muted-foreground dark:text-slate-500 truncate">{me?.email ?? ""}</p>
                  </div>
                </div>
              </div>

              {/* Multi-company list — only shown when user has 2+ tenants */}
              {tenants && tenants.length > 1 && (
                <div className="py-1 border-b border-border dark:border-slate-700">
                  <p className="px-4 pt-2 pb-1 text-[10px] font-semibold text-muted-foreground dark:text-slate-500 uppercase tracking-wider">
                    Switch Company
                  </p>
                  {tenants.map((t) => {
                    const isActive = t.tenantId === (selectedTenantId ?? tenants[0]?.tenantId);
                    return (
                      <button
                        key={t.tenantId}
                        data-testid={`tenant-item-${t.tenantId}`}
                        data-active={isActive ? "true" : "false"}
                        onClick={() => handleSwitchTenant(t.tenantId)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/50 dark:hover:bg-slate-700 transition-colors"
                      >
                        <div
                          className="h-7 w-7 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                          style={{ backgroundColor: t.primaryColor ?? "#0a2d4a" }}
                        >
                          {t.name[0]}
                        </div>
                        <span className="flex-1 text-sm text-foreground/80 dark:text-slate-300 truncate">{t.name}</span>
                        {isActive && <Check className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}

              {(canSee(["organization"]) || canSee(["users", "roles"])) && (
                <div className="py-1">
                  {canSee(["organization"]) && (
                    <Link
                      href="/organization"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground/80 dark:text-slate-300 hover:bg-muted/50 dark:hover:bg-slate-700 transition-colors"
                      onClick={() => setCompanySwitcherOpen(false)}
                    >
                      <Building2 className="h-4 w-4 text-muted-foreground dark:text-slate-500" />
                      Company Settings
                    </Link>
                  )}
                  {canSee(["users", "roles"]) && (
                    <Link
                      href="/users"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground/80 dark:text-slate-300 hover:bg-muted/50 dark:hover:bg-slate-700 transition-colors"
                      onClick={() => setCompanySwitcherOpen(false)}
                    >
                      <Shield className="h-4 w-4 text-muted-foreground dark:text-slate-500" />
                      Users & Roles
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className={`h-5 w-px bg-border ${searchOpen ? 'hidden md:block' : ''}`} />

        {/* Product label */}
        <img
          src={`${import.meta.env.BASE_URL.replace(/\/$/, "")}/logo.png`}
          alt="Alea ERP"
          className="h-5 w-auto select-none hidden md:block opacity-90"
        />

        {/* Breadcrumbs (module pages) */}
        {!isLaunchpad && breadcrumbs && breadcrumbs.length > 0 && (
          <nav className={`items-center gap-1 ml-2 ${searchOpen ? 'hidden md:flex' : 'flex'}`}>
            <Link href="/dashboard" className="text-muted-foreground hover:text-foreground transition-colors">
              <LayoutGrid className="h-3.5 w-3.5" />
            </Link>
            {breadcrumbs.map((crumb, i) => (
              <span key={`${crumb.label}-${i}`} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
                {crumb.href && i < breadcrumbs.length - 1 ? (
                  <Link
                    href={crumb.href}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="text-xs text-foreground font-medium">{crumb.label}</span>
                )}
              </span>
            ))}
          </nav>
        )}

        <div className={searchOpen ? 'hidden md:block md:flex-1' : 'flex-1'} />

        {/* Global Search */}
        <div className={`flex items-center relative ${searchOpen ? 'flex-1 md:flex-initial' : ''}`} data-search-container>
          {searchOpen ? (
            <div className="relative w-full">
              <div className="flex items-center bg-muted rounded-md px-3 py-1 gap-2 border border-border w-full md:w-64">
                <Search className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search pages, settings, people, anything…"
                  className="bg-transparent text-foreground text-xs placeholder:text-muted-foreground outline-none flex-1 min-w-0"
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      closeSearch();
                    } else if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setSelectedIndex((i) => Math.min(i + 1, flatResults.length - 1));
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setSelectedIndex((i) => Math.max(i - 1, -1));
                    } else if (e.key === "Enter" && selectedIndex >= 0 && flatResults[selectedIndex]) {
                      e.preventDefault();
                      navigateTo(flatResults[selectedIndex].href);
                    }
                  }}
                />
                {isFetching && (
                  <RefreshCw className="h-3 w-3 text-muted-foreground animate-spin flex-shrink-0" />
                )}
                <button
                  onClick={closeSearch}
                  className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>

              {/* Dropdown */}
              {debouncedQuery.length >= 2 && (
                <div
                  ref={dropdownRef}
                  className="absolute top-full right-0 mt-1.5 w-80 bg-card dark:bg-slate-800 rounded-xl shadow-2xl border border-border dark:border-slate-700 z-50 overflow-hidden max-h-96 overflow-y-auto"
                >
                  {groups.length === 0 && !isFetching ? (
                    <div className="px-4 py-8 text-center">
                      <Search className="h-6 w-6 text-muted-foreground/60 dark:text-slate-600 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground dark:text-slate-500">No results for "{debouncedQuery}"</p>
                    </div>
                  ) : (
                    (() => {
                      let globalIdx = 0;
                      return groups.map(({ group, items }) => (
                        <div key={group}>
                          <div className="px-3 pt-3 pb-1">
                            <p className="text-[10px] font-semibold text-muted-foreground dark:text-slate-500 uppercase tracking-wider">{group}</p>
                          </div>
                          {items.map((item) => {
                            const itemIdx = globalIdx++;
                            const isActive = itemIdx === selectedIndex;
                            return (
                              <button
                                key={item.id}
                                className={`w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors ${
                                  isActive ? "bg-blue-50 dark:bg-blue-900/40" : "hover:bg-muted/50 dark:hover:bg-slate-700"
                                }`}
                                onMouseEnter={() => setSelectedIndex(itemIdx)}
                                onClick={() => navigateTo(item.href)}
                              >
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium truncate ${isActive ? "text-blue-700 dark:text-blue-300" : "text-foreground dark:text-slate-200"}`}>
                                    {item.label}
                                  </p>
                                  {item.sublabel && (
                                    <p className="text-xs text-muted-foreground dark:text-slate-500 truncate">{item.sublabel}</p>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ));
                    })()
                  )}
                  <div className="px-3 py-2 border-t border-border dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50">
                    <p className="text-[10px] text-muted-foreground dark:text-slate-500">↑↓ navigate · Enter select · Esc close</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors border border-border bg-muted/50"
              title="Search (⌘K or Ctrl+K)"
            >
              <Search className="h-3.5 w-3.5" />
              <span className="text-xs hidden lg:inline text-muted-foreground">Search...</span>
              <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.2 text-[10px] font-medium bg-muted text-muted-foreground rounded border border-border">
                <span>⌘</span>K
              </kbd>
            </button>
          )}
        </div>

        {/* Notifications & User */}
        <div className={searchOpen ? 'hidden md:flex items-center gap-2' : 'flex items-center gap-2'}>
        <NotificationBell />
        {/* Attendance widget only for non-admin users (employees without HR/Finance/CRM access) */}
        {(canSee(["hr"]) || canSee(["finance"]) || canSee(["crm"])) ? null : <AttendanceHeaderWidget />}
        <button
          onClick={toggleTheme}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        <div className="h-5 w-px bg-border mx-1" />
        <div className="[&_.cl-userButtonTrigger]:ring-0 [&_.cl-userButtonTrigger]:rounded-full [&_.cl-userButtonTrigger]:hover:opacity-90">
          <UserButton
            appearance={{
              elements: {
                userButtonPopoverCard: "shadow-xl border border-slate-200 rounded-xl overflow-hidden bg-white",
                userButtonPopoverActionButton: "hover:bg-slate-50 rounded-lg",
                userButtonPopoverActionButtonText: "text-slate-700 font-medium",
                userButtonPopoverFooter: "hidden",
              },
            }}
            userProfileProps={{
              appearance: {
                variables: {
                  colorPrimary: "#2563eb",
                  borderRadius: "0.625rem",
                  // Ensure strong contrast on dark backgrounds
                  colorText: "#1e293b",
                  colorTextSecondary: "#475569",
                  colorBackground: "#ffffff",
                  colorInputBackground: "#ffffff",
                  colorInputText: "#1e293b",
                },
                elements: {
                  // ── Primary form buttons ──
                  formButtonPrimary: "bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white normal-case",
                  badge: "bg-blue-50 text-blue-600 border border-blue-100",

                  // ── Mobile nav toggle — clearly visible & tappable ──
                  navbarMobileMenuButton: "text-slate-700 hover:bg-slate-100 rounded-lg",

                  // ── Left navigation panel — high contrast on light bg ──
                  navbar: "bg-white border-r border-slate-200",
                  navbarButtonText: "text-slate-800 font-medium",
                  navbarButton: "hover:bg-slate-50 rounded-lg text-slate-700",
                  navbarButtonTextActive: "text-blue-600 font-semibold",
                  navbarButtonIcon: "text-slate-500",
                  navbarButtonIconActive: "text-blue-600",

                  // ── Page headings & section titles ──
                  pageHeading: "text-slate-900 font-bold",
                  sectionTitle: "text-slate-800 font-semibold",
                  sectionSubtitle: "text-slate-500",

                  // ── Delete / danger section — clear alignment & spacing ──
                  dangerSection: "flex flex-col gap-3 pt-4 mt-4 border-t border-red-200",
                  dangerTitle: "text-red-600 font-semibold text-base",
                  dangerDescription: "text-slate-600 text-sm leading-relaxed",
                  dangerButtonText: "text-red-600 hover:text-white hover:bg-red-600 border border-red-300 hover:border-red-600 rounded-lg px-4 py-2 font-medium transition-colors",
                  dangerListItem: "flex items-center justify-between gap-4 py-3",

                  // ── General content readability ──
                  card: "bg-white border border-slate-200 rounded-xl",
                  profileSection: "text-slate-700",
                  accordionTrigger: "text-slate-800 font-medium",
                  footerTitle: "text-slate-500 text-xs",
                  footer: "text-slate-500",
                },
              },
            }}
          />
        </div>
        </div>
      </header>

      {/* Shell body: left module rail + content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Mobile sidebar overlay */}
        {mobileSidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}

        {/* Mobile slide-out drawer */}
        <div
          className={`fixed top-0 left-0 h-full z-50 md:hidden flex flex-col overflow-hidden transition-transform duration-300 ease-in-out ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
          style={{ backgroundColor: shellBg, width: 240 }}
        >
          {/* Drawer header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0" style={{ backgroundColor: shellBg }}>
            <span className="text-white font-semibold text-sm">Navigation</span>
            <button
              onClick={() => setMobileSidebarOpen(false)}
              className="h-7 w-7 flex items-center justify-center rounded text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Drawer nav items */}
          <nav className="flex-1 overflow-y-auto overscroll-contain py-2 scrollbar-hide" style={{ backgroundColor: railBg }}>
            {visibleRail.map((item) => {
              const { id, icon: Icon, label, href } = item;
              const active =
                href === "/dashboard"
                  ? isLaunchpad
                  : location === href || location.startsWith(href + "/");
              const isModuleLocked = isModulePackageLocked(item, allowedModules);

              if (isModuleLocked) {
                return (
                  <button
                    key={id}
                    onClick={() => {
                      setMobileSidebarOpen(false);
                      setLockedModuleName(packageKeysForRail(item)?.[0] ?? label);
                      setLockedModalOpen(true);
                    }}
                    className="flex items-center justify-between w-full mx-2 px-3 py-2.5 rounded-lg text-white/40 hover:bg-white/5 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-4 w-4 flex-shrink-0 opacity-60" />
                      <span className="text-sm font-medium opacity-60">{label}</span>
                    </div>
                    <Lock className="h-3.5 w-3.5 text-amber-400" />
                  </button>
                );
              }

              return (
                <Link
                  key={id}
                  href={href}
                  data-testid={`mobile-nav-item-${id}`}
                  onClick={() => setMobileSidebarOpen(false)}
                  className={`flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg transition-all ${
                    active
                      ? "bg-white/20 text-white"
                      : "text-white/60 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium">{label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Left Module Rail — collapsible, desktop only */}
        <nav
          className={`hidden md:flex flex-shrink-0 h-full overflow-y-auto overscroll-contain scrollbar-hide flex-col pt-2 pb-4 gap-1 [@media(max-height:860px)]:gap-0.5 z-20 shadow-sm transition-[width] duration-200 ease-in-out ${railExpanded ? "w-52 items-stretch px-2" : "w-12 items-center"}`}
          style={{ backgroundColor: railBg }}
        >
          {/* Collapse / expand toggle */}
          <button
            type="button"
            onClick={() => setRailExpanded((v) => { localStorage.setItem("railExpanded", String(!v)); return !v; })}
            aria-label={railExpanded ? "Collapse sidebar" : "Expand sidebar"}
            title={railExpanded ? "Collapse sidebar" : "Expand sidebar"}
            data-testid="rail-toggle"
            className={`flex flex-shrink-0 items-center h-9 rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors mb-1 ${railExpanded ? "justify-start gap-3 px-2.5" : "justify-center w-10 mx-auto"}`}
          >
            <ChevronsLeft className={`h-[18px] w-[18px] transition-transform duration-200 ${railExpanded ? "" : "rotate-180"}`} />
            {railExpanded && <span className="text-xs font-medium">Collapse</span>}
          </button>

          {visibleRail.map((item) => {
            const { id, icon: Icon, label, href } = item;
            const active =
              href === "/dashboard"
                ? isLaunchpad
                : location === href || location.startsWith(href + "/");
            const isModuleLocked = isModulePackageLocked(item, allowedModules);

            if (isModuleLocked) {
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setLockedModuleName(packageKeysForRail(item)?.[0] ?? label);
                    setLockedModalOpen(true);
                  }}
                  title={railExpanded ? `${label} (Subscription Upgrade Required)` : `${label} 🔒`}
                  className={`relative group flex flex-shrink-0 items-center rounded-lg transition-all duration-150 text-white/40 hover:bg-amber-500/10 hover:text-amber-300 ${
                    railExpanded
                      ? "gap-3 px-2.5 h-10 [@media(max-height:860px)]:h-9 justify-between"
                      : "justify-center h-10 w-10 [@media(max-height:860px)]:h-9 [@media(max-height:740px)]:h-8"
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Icon className="h-[18px] w-[18px] flex-shrink-0 opacity-60" />
                    {railExpanded && <span className="text-sm font-medium truncate opacity-70">{label}</span>}
                  </div>
                  {railExpanded ? (
                    <Lock className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                  ) : (
                    <span className="pointer-events-none absolute left-full ml-3 px-2.5 py-1.5 text-xs font-medium text-white bg-popover/95 rounded-md shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50 flex items-center gap-1.5">
                      <span>{label}</span>
                      <Lock className="h-3 w-3 text-amber-400" />
                    </span>
                  )}
                </button>
              );
            }

            return (
              <Link
                key={id}
                href={href}
                title={railExpanded ? undefined : label}
                data-testid={`nav-item-${id}`}
                className={`relative group flex flex-shrink-0 items-center rounded-lg transition-all duration-150 ${
                  railExpanded
                    ? "gap-3 px-2.5 h-10 [@media(max-height:860px)]:h-9"
                    : "justify-center h-10 w-10 [@media(max-height:860px)]:h-9 [@media(max-height:740px)]:h-8"
                } ${
                  active
                    ? "bg-white/20 text-white"
                    : "text-white/45 hover:bg-white/10 hover:text-white"
                }`}
              >
                {active && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-card rounded-r-full" />
                )}
                <Icon className="h-[18px] w-[18px] flex-shrink-0 [@media(max-height:740px)]:h-4 [@media(max-height:740px)]:w-4" />
                {railExpanded ? (
                  <span className="text-sm font-medium truncate">{label}</span>
                ) : (
                  /* Tooltip (collapsed only) */
                  <span className="pointer-events-none absolute left-full ml-3 px-2.5 py-1.5 text-xs font-medium text-white bg-popover/95 rounded-md shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50">
                    {label}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto min-w-0">
          {/* While /api/me is still in-flight, permissions are unknown — show a
              neutral spinner so the "Access restricted" screen never flashes for
              authenticated users who simply lack a loaded permission set yet.  */}
          {mePending ? (
            <div className="h-full flex items-center justify-center">
              <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-primary" />
            </div>
          ) : routeAllowed ? (
            children
          ) : (
            <div className="h-full flex items-center justify-center px-6 py-16">
              <div className="text-center max-w-md">
                <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-muted flex items-center justify-center">
                  <Shield className="h-7 w-7 text-muted-foreground" />
                </div>
                <h2 className="text-lg font-semibold text-foreground">Access restricted</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  You don't have permission to view this page. If you think this is a
                  mistake, contact your administrator to update your role.
                </p>
                <Link
                  href="/dashboard"
                  className="mt-5 inline-flex h-9 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:opacity-90 transition-opacity"
                >
                  Back to Dashboard
                </Link>
              </div>
            </div>
          )}
        </main>
      </div>

      <UpgradePackageModal
        isOpen={lockedModalOpen}
        onClose={() => setLockedModalOpen(false)}
        moduleName={lockedModuleName}
        currentPackageName={tenantPackage?.name}
      />
    </div>
  );
}
